import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Calculator,
  Eye,
  Loader2,
  AlertTriangle,
  Ban,
  Unlock,
  ToggleLeft,
  ToggleRight,
  X,
  Download,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import { RotateCcw } from 'lucide-react';
import WarningPopup from '../../../components/WarningPopup';
import { formatDate } from '../../../utils/stringUtils';
import { getSalaryStructures, deleteSalaryStructure, updateSalaryStructureStatus } from '../../../api/salaryStructureApi';
import { toast, Toaster } from 'react-hot-toast';
import useUrlTableState from '../../../hooks/useUrlTableState';

const SalaryStructures = (props) => {
  const navigate = useNavigate();
  const searchInputRef = useRef(null);

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 20,
    search: '',
    statusFilter: 'all',
    startDate: '',
    endDate: ''
  }), []);

  const {
    filters: structureFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateStructureFilter,
    updatePage: updateStructurePage,
    updateLimit: updateStructureLimit,
    resetFilters: resetStructureFilters,
  } = useUrlTableState(defaultFilters);

  const page = structureFilters.page;
  const limit = structureFilters.limit;
  const search = searchQuery;
  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;

  const statusFilter = structureFilters.statusFilter || 'all';
  const setStatusFilter = useCallback((statusFilter) => {
    updateStructureFilter({ statusFilter });
  }, [updateStructureFilter]);

  const filterStartDate = structureFilters.startDate || '';
  const filterEndDate = structureFilters.endDate || '';

  const setDateRange = useCallback((range) => {
    updateStructureFilter({ startDate: range.startDate || '', endDate: range.endDate || '' });
  }, [updateStructureFilter]);

  const setFilterStartDate = useCallback((startDate) => {
    updateStructureFilter({ startDate });
  }, [updateStructureFilter]);

  const setFilterEndDate = useCallback((endDate) => {
    updateStructureFilter({ endDate });
  }, [updateStructureFilter]);

  const setPage = useCallback((page) => {
    updateStructurePage(page);
  }, [updateStructurePage]);

  const setLimit = useCallback((limit) => {
    updateStructureLimit(limit);
  }, [updateStructureLimit]);

  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const [selectedStructure, setSelectedStructure] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [structureToDelete, setStructureToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);
  const queryClient = useQueryClient();

  // Fetch salary structures
  const {
    data: structuresData,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['salaryStructures', page, limit, searchTerm, statusFilter, filterStartDate, filterEndDate],
    queryFn: async () => {
      const response = await getSalaryStructures({
        page,
        limit,
        search: searchTerm.trim() || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        startDate: filterStartDate || undefined,
        endDate: filterEndDate || undefined,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });
      return response.data || response;
    },
    onError: (err) => {
      toast.error('Failed to load salary structures');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: deleteSalaryStructure,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['salaryStructures'] });
      setShowDeleteModal(false);
      setStructureToDelete(null);
    },
    onError: (err) => {
      const message = err?.response?.data?.message || err?.message || 'Failed to delete salary structure';
      toast.error(message);
    }
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => updateSalaryStructureStatus(id, status),
    onMutate: ({ id }) => {
      setStatusUpdatingId(id);
    },
    onSuccess: (_data, variables) => {
      const nextStatusLabel = variables.status === 'active' ? 'activated' : 'deactivated';
      toast.success(`Salary structure ${nextStatusLabel} successfully.`);
      queryClient.invalidateQueries({ queryKey: ['salaryStructures'] });
      setStatusUpdatingId(null);
    },
    onError: (err) => {
      const message = err?.response?.data?.message || err?.message || 'Failed to update status';
      toast.error(message);
      setStatusUpdatingId(null);
    }
  });

  const structures = structuresData?.structures || [];
  const pagination = structuresData?.pagination || {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false
  };

  const formatCurrency = (value) =>
    typeof value === 'number' ? `₹${value.toLocaleString('en-IN')}` : '-';

  const componentList = selectedStructure?.components ?? [];

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortedStructures = () => {
    if (!structures) return [];
    if (!sortField) return structures;

    return [...structures].sort((a, b) => {
      let aValue, bValue;
      switch (sortField) {
        case 'name':
          aValue = (a.name || '').toLowerCase();
          bValue = (b.name || '').toLowerCase();
          break;
        case 'department':
          aValue = typeof a.department === 'object'
            ? (a.department?.name || '').toLowerCase()
            : (a.department || '').toLowerCase();
          bValue = typeof b.department === 'object'
            ? (b.department?.name || '').toLowerCase()
            : (b.department || '').toLowerCase();
          break;
        case 'code':
          aValue = (a.code || '').toLowerCase();
          bValue = (b.code || '').toLowerCase();
          break;
        case 'ctc':
          aValue = Number(a.ctc || 0);
          bValue = Number(b.ctc || 0);
          break;
        case 'netPay':
          aValue = Number(a.netPay || 0);
          bValue = Number(b.netPay || 0);
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'createdAt':
          aValue = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          bValue = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleCreate = () => {
    if (props.onAdd) {
      props.onAdd();
    } else {
      navigate('/salary_structure/create');
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleClearFilters = () => {
    resetStructureFilters();
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Debounce search term - useUrlTableState handles this internally

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLimitChange = (value) => {
    const parsedValue = typeof value === 'number' ? value : parseInt(value?.target?.value, 10);
    if (Number.isNaN(parsedValue)) return;
    setLimit(parsedValue);
  };

  const handleViewStructure = (structure) => {
    setSelectedStructure(structure);
    setShowViewModal(true);
  };

  const handleEditStructure = (structure) => {
    if (!structure?.id) return;
    if (props.onEdit) {
      props.onEdit(structure.id);
    } else {
      navigate(`/salary_structure/create?structureId=${structure.id}`);
    }
  };

  const handleCloseViewModal = () => {
    setSelectedStructure(null);
    setShowViewModal(false);
  };

  const openDeleteModal = (structure) => {
    setStructureToDelete(structure);
    setIsBulkDelete(false);
    setShowDeleteModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setStructureToDelete(null);
    setIsBulkDelete(false);
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = async () => {
    if (!structureToDelete && !isBulkDelete) return;

    try {
      if (isBulkDelete) {
        const ids = Array.from(selectedRows);
        let successCount = 0;
        let failCount = 0;

        for (const id of ids) {
          try {
            await deleteMutation.mutateAsync(id);
            successCount++;
          } catch (error) {
            failCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} salary structure(s) deleted successfully.`);
          setSelectedRows(new Set());
        }
        if (failCount > 0) {
          toast.error(`${failCount} salary structure(s) could not be deleted.`);
        }
      } else {
        await deleteMutation.mutateAsync(structureToDelete.id);
        toast.success('Salary structure deleted successfully.');
      }
      setIsBulkDelete(false);
      setShowDeleteModal(false);
    } catch (error) {
    }
  };

  const handleToggleStatus = (structure) => {
    if (!structure?.id) return;
    const nextStatus = structure.status === 'active' ? 'inactive' : 'active';
    statusMutation.mutate({ id: structure.id, status: nextStatus });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-red-100 text-red-700';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Row selection handlers
  const handleSelectAll = (e) => {
    const currentPageIds = structures.map(s => s.id);
    const selectedOnPage = currentPageIds.filter(id => selectedRows.has(id));

    if (selectedOnPage.length > 0) {
      // If any selected on current page, clear EVERYTHING
      setSelectedRows(new Set());
    } else {
      // Otherwise select all on current page
      const newSelected = new Set(selectedRows);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedRows(newSelected);
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  // Clear selections when filters change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [page, limit, search, statusFilter, filterStartDate, filterEndDate]);

  // Click-outside handler for inline export format bar
  useEffect(() => {
    const onMouseDown = (event) => {
      if (!showInlineSelectedFormats) return;
      const target = event.target;
      const wrap = selectedExportWrapRef.current;
      const bar = target.closest('[data-export-format-bar]');
      if (wrap && !wrap.contains(target) && !bar) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  // Build base export filters
  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      search: search || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      startDate: filterStartDate || undefined,
      endDate: filterEndDate || undefined,
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [search, statusFilter, filterStartDate, filterEndDate]);

  // Get current page structure IDs
  const currentPageStructureIds = useMemo(
    () =>
      structures
        .map((s) => {
          const raw = s?._id ?? s?.id;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [structures]
  );

  // Unified backend export function using /exports/generate endpoint
  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageStructureIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'salary-structures',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? page,
            limit: overrideLimit ?? limit,
            sortBy: sortField || 'name',
            sortOrder,
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
            pdfFormat: format === 'pdf' ? 'a4' : undefined,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `salary-structures-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      page,
      limit,
      sortField,
      sortOrder,
      currentPageStructureIds,
    ]
  );

  // Handle export for selected structures with format selection
  const handleSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected structures to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  // Handle scoped export from modal
  const handleScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              startDate,
              endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: limit,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageStructureIds,
          });
        } else {
          return;
        }

        toast.success('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      structures.length,
      baseExportFilters,
      requestBackendExport,
      limit,
      currentPageStructureIds,
    ]
  );

  // Export click handler
  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };



  return (
    <div className="p-6">
      <Toaster
        position="top-right"
        reverseOrder={false}
        gutter={8}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
            borderRadius: '8px',
            padding: '12px 16px',
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
            style: {
              background: '#ef4444',
              color: '#fff',
            },
          },
        }}
      />
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Salary Structures</h1>
          <p className="text-gray-600">Manage employee salary structures and components</p>
        </div>
        <div className="flex items-center space-x-4">
          {selectedRows.size > 0 && (
            <PermissionGate permission="payroll" action="delete">
              <Button
                variant="primary"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={handleBulkDelete}
                className="bg-red-600 hover:bg-red-700 border-red-600"
              >
                Delete ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="payroll" action="read">
            <div ref={selectedExportWrapRef} className="relative">
              <Button
                variant="outline"
                icon={<Download className="h-4 w-4" />}
                onClick={handleExportClick}
              >
                {selectedRows.size > 0
                  ? `Export Selected (${selectedRows.size})`
                  : 'Export'}
              </Button>
              {showInlineSelectedFormats && (
                <SelectedExportFormatBar
                  onFormatSelect={handleSelectedFormatExport}
                  loading={exportLoading}
                  onClose={() => setShowInlineSelectedFormats(false)}
                  onMouseEnter={() => setInlineFormatPanelEntered(true)}
                  onMouseLeave={() => setInlineFormatPanelEntered(false)}
                />
              )}
            </div>
          </PermissionGate>
          <PermissionGate permission="payroll" action="create">
            <Button
              onClick={handleCreate}
              className="bg-brand text-white hover:bg-brand-dark transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Structure
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4 w-full">
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={handleSearch}
              placeholder="Search structures or Departments..."
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            />
            {searchTerm && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSearch('');
                  setPage(1);
                  if (searchInputRef.current) {
                    searchInputRef.current.focus();
                  }
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
                title="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex-shrink-0">
            <DateRangeFilter
              startDate={filterStartDate}
              endDate={filterEndDate}
              allowFutureDates={false}
              onChange={setDateRange}
            />
          </div>

          <div className="flex-shrink-0">
            <select
              value={statusFilter}
              onChange={handleStatusFilter}
              className="px-3 py-2 border border-gray-300 rounded-lg outline-none shadow-none focus:shadow-none focus-visible:shadow-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          {(searchTerm || statusFilter !== 'all' || filterStartDate || filterEndDate) && (
            <div className="flex-shrink-0">
              <button
                onClick={handleClearFilters}
                className="flex items-center space-x-2 px-4 py-[9px] border border-gray-300 rounded-lg text-gray-700 hover:bg-blue-50/30 transition-colors"
                title="Clear all filters"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Clear</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Structures List */}
      <div className="space-y-4">
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand" />
              <span className="ml-3 text-gray-600">Loading salary structures...</span>
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center py-12">
              <p className="text-red-600 mb-4">Failed to load salary structures</p>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors"
              >
                Retry
              </button>
            </div>
          ) : structures.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Calculator className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-gray-600">No salary structures found</p>
              <p className="text-sm text-gray-500 mt-2">
                {search || statusFilter !== 'all' ? 'Try adjusting your filters' : 'Create your first salary structure'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-transparent" style={{ backgroundColor: "rgba(59, 130, 246, 0.14)" }}>
                    <tr>
                      <th className="px-3 py-2 text-left">
                        <input
                          type="checkbox"
                          checked={structures.length > 0 && selectedRows.size === structures.length}
                          onChange={handleSelectAll}
                          className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-0 cursor-pointer"
                        />
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Structure</span>
                          {sortField === 'name' ? (
                            sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                          ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                        onClick={() => handleSort('department')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Department</span>
                          {sortField === 'department' ? (
                            sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                          ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                        onClick={() => handleSort('code')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Code</span>
                          {sortField === 'code' ? (
                            sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                          ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                        onClick={() => handleSort('ctc')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>CTC</span>
                          {sortField === 'ctc' ? (
                            sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                          ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                        onClick={() => handleSort('netPay')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Net Pay</span>
                          {sortField === 'netPay' ? (
                            sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                          ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                        onClick={() => handleSort('status')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Status</span>
                          {sortField === 'status' ? (
                            sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                          ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/60 select-none"
                        onClick={() => handleSort('createdAt')}
                      >
                        <div className="flex items-center space-x-1">
                          <span>Created At</span>
                          {sortField === 'createdAt' ? (
                            sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 flex-shrink-0 text-[#0F2942]" /> : <ArrowDown className="w-3 h-3 flex-shrink-0 text-[#0F2942]" />
                          ) : <ArrowUpDown className="w-3 h-3 flex-shrink-0 text-gray-400" />}
                        </div>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getSortedStructures().map((structure) => (
                      <tr key={structure.id} className="hover:bg-blue-50/30">
                        <td className="px-3 py-2 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedRows.has(structure.id)}
                            onChange={() => handleSelectRow(structure.id)}
                            className="h-4 w-4 text-brand border-gray-300 rounded focus:ring-0 cursor-pointer"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="max-w-xs">
                            <div
                              className="text-sm font-medium text-gray-900 capitalize truncate"
                              title={structure.name}
                            >
                              {structure.name}
                            </div>
                            <div
                              className="text-sm text-gray-500 uppercase tracking-wide truncate"
                              title={structure.code}
                            >
                              {structure.code}
                            </div>
                          </div>
                        </td>
                        <td
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize truncate max-w-[180px]"
                          title={typeof structure.department === 'object' ? structure.department?.name : structure.department}
                        >
                          {typeof structure.department === 'object' ? structure.department?.name : structure.department}
                        </td>
                        <td
                          className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 uppercase truncate max-w-[150px]"
                          title={structure.code}
                        >
                          {structure.code}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(structure.ctc)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(structure.netPay)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(structure.status)}`}
                          >
                            <span className="capitalize">{structure.status}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {structure.createdAt ?
                            formatDate(structure.createdAt, { includeTime: true }) :
                            'N/A'
                          }
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              className="text-brand hover:text-green-700 transition-colors"
                              aria-label="View salary structure"
                              onClick={() => handleViewStructure(structure)}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <PermissionGate permission="payroll" action="update">
                              <button
                                className={`p-1.5 rounded-lg ${structure.status === 'active'
                                  ? 'text-yellow-700 hover:bg-yellow-50'
                                  : 'text-green-700 hover:bg-green-50'
                                  } transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
                                onClick={() => handleToggleStatus(structure)}
                                disabled={statusUpdatingId === structure.id && statusMutation.isPending}
                                aria-label={structure.status === 'active' ? 'Block structure' : 'Unblock structure'}
                                title={structure.status === 'active' ? 'Block structure' : 'Unblock structure'}
                              >
                                {statusUpdatingId === structure.id && statusMutation.isPending ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : structure.status === 'active' ? (
                                  <ToggleLeft className="w-5 h-5" />
                                ) : (
                                  <ToggleRight className="w-5 h-5" />
                                )}
                              </button>
                            </PermissionGate>
                            <PermissionGate permission="payroll" action="update">
                              <button
                                className="text-[#0F2942] hover:text-blue-900 transition-colors"
                                aria-label="Edit salary structure"
                                onClick={() => handleEditStructure(structure)}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            </PermissionGate>
                            <PermissionGate permission="payroll" action="delete">
                              <button
                                className="text-red-600 hover:text-red-900 transition-colors"
                                aria-label="Delete structure"
                                onClick={() => openDeleteModal(structure)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </PermissionGate>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {pagination.total > limit && (
                <PaginationControls
                  page={pagination.page}
                  limit={limit}
                  total={pagination.total}
                  hasNextPage={pagination.hasNextPage}
                  hasPrevPage={pagination.hasPrevPage}
                  onPageChange={handlePageChange}
                  onLimitChange={handleLimitChange}
                  border={false}
                />
              )}
            </>
          )}
        </div>
      </div>
      {/* View Structure Modal */}
      {showViewModal && selectedStructure && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="max-w-full">
                <p className="text-sm uppercase text-gray-500 tracking-wide">Salary Structure</p>
                <h2 className="text-2xl font-semibold text-gray-900 break-words whitespace-normal capitalize">{selectedStructure.name}</h2>
                <p className="text-sm text-gray-500">Structure ID: {selectedStructure.structureId}</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gray-50 rounded-xl p-4 min-w-0">
                  <p className="text-xs uppercase text-gray-500">Structure Code</p>
                  <p className="text-lg font-semibold text-gray-900 uppercase break-words" title={selectedStructure.code}>{selectedStructure.code}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 min-w-0">
                  <p className="text-xs uppercase text-gray-500">Department</p>
                  <p className="text-lg font-semibold text-gray-900 capitalize break-words" title={typeof selectedStructure.department === 'object' ? selectedStructure.department?.name : selectedStructure.department}>{typeof selectedStructure.department === 'object' ? selectedStructure.department?.name : selectedStructure.department}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 min-w-0">
                  <p className="text-xs uppercase text-gray-500">Level</p>
                  <p className="text-lg font-semibold text-gray-900 capitalize break-words" title={selectedStructure.grade}>{selectedStructure.grade}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 min-w-0">
                  <p className="text-xs uppercase text-gray-500">CTC</p>
                  <p className="text-lg font-semibold text-gray-900">{formatCurrency(selectedStructure.ctc)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 min-w-0">
                  <p className="text-xs uppercase text-gray-500">Net Pay</p>
                  <p className="text-lg font-semibold text-gray-900">{formatCurrency(selectedStructure.netPay)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 flex flex-col gap-2 min-w-0">
                  <p className="text-xs uppercase text-gray-500">Status</p>
                  <span className={`inline-flex px-3 py-1 rounded-full text-sm font-semibold capitalize self-start ${getStatusColor(selectedStructure.status)}`}>
                    {selectedStructure.status}
                  </span>
                </div>
              </div>

              <div className="border border-gray-200 rounded-2xl overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Components</h3>
                  <p className="text-sm text-gray-500">{componentList.length} items</p>
                </div>
                <div className="overflow-visible">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-white">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Component</th>
                        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Percentage</th>
                        <th className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {componentList.map((component) => (
                        <tr key={component._id}>
                          <td className="px-4 py-3 text-sm text-gray-900 font-medium capitalize">{component.name}</td>
                          <td className="px-4 py-3 text-sm capitalize">
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${component.type === 'earning'
                              ? 'bg-green-50 text-green-700'
                              : component.type === 'deduction'
                                ? 'bg-red-50 text-red-700'
                                : 'bg-blue-50 text-[#0B2033]'
                              }`}>
                              {component.type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-600">
                            {component.type === 'reimbursement'
                              ? '—'
                              : component.percentageValue
                                ? `${component.percentageValue}% CTC`
                                : '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 font-semibold">{formatCurrency(component.value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end">
              <button
                onClick={handleCloseViewModal}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-white transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <WarningPopup
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDelete}
        title={isBulkDelete ? "Delete Salary Structures" : "Delete Salary Structure"}
        message={
          isBulkDelete
            ? `Are you sure you want to delete ${selectedRows.size} selected salary structure(s)? This action cannot be undone.`
            : (structureToDelete
              ? `Are you sure you want to delete the salary structure "${structureToDelete.code}"? This action cannot be undone.`
              : "Are you sure you want to delete this salary structure?")
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      {/* Export Modal */}
      {showExportModal && (
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          scopedFormatExport={{
            onExport: handleScopedExport,
          }}
          loading={exportLoading}
          title="Export Salary Structures"
          showDateRange={true}
        />
      )}
    </div>
  );
};

export default SalaryStructures;
